import { useNavigate, type NavigateFunction } from "react-router-dom";
import { useRoutes, useLocation } from "react-router-dom";
import { useEffect, useLayoutEffect, useMemo } from "react";
import routes from "./config";
import OrganizationJsonLd from "../components/seo/OrganizationJsonLd";
import BreadcrumbJsonLd from "../components/seo/BreadcrumbJsonLd";
import StaticRouteDocumentHead from "../components/seo/StaticRouteDocumentHead";
import { getBreadcrumbsForPathname } from "../utils/breadcrumbJsonLd";
import { defaultOriginForJsonLd } from "../utils/organizationJsonLd";
import { SITE_DEFAULT_ORIGIN } from "../config/siteOrganization";
import { normalizePathname } from "../config/staticPageMeta";

let navigateResolver: (navigate: ReturnType<typeof useNavigate>) => void;

declare global {
  interface Window {
    REACT_APP_NAVIGATE: ReturnType<typeof useNavigate>;
  }
}

export const navigatePromise = new Promise<NavigateFunction>((resolve) => {
  navigateResolver = resolve;
});

export function AppRoutes() {
  const element = useRoutes(routes);
  const location = useLocation();
  /** 条件分岐の内側で呼ばない（レンダーごとにフック数が変わると白画面・不整合の原因になる） */
  const navigate = useNavigate();
  const origin = defaultOriginForJsonLd();
  const breadcrumbItems = useMemo(
    () => getBreadcrumbsForPathname(location.pathname, origin),
    [location.pathname, origin]
  );

  useEffect(() => {
    window.REACT_APP_NAVIGATE = navigate;
    navigateResolver(navigate);
  }, [navigate]);

  /**
   * 一覧など長いページから別ルートへ遷移したとき、スクロール位置が残ると
   * 記事先頭が見えない（土田様報告の事象）。パス変更時は常に先頭へ。
   * useLayoutEffect で描画前に戻し、一瞬だけ中途表示されるチラつきを抑える。
   */
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  /** SPA 各ルートの canonical を head に反映（SEO・重複URL対策） */
  useEffect(() => {
    const origin = SITE_DEFAULT_ORIGIN.replace(/\/$/, "");
    const raw =
      location.pathname.startsWith("/") ? location.pathname : `/${location.pathname}`;
    const path = normalizePathname(raw);
    const href = path === "/" ? `${origin}/` : `${origin}${path}`;
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.rel = "canonical";
      document.head.appendChild(link);
    }
    link.href = href;
  }, [location.pathname]);

  return (
    <>
      <OrganizationJsonLd />
      {breadcrumbItems && <BreadcrumbJsonLd items={breadcrumbItems} />}
      {element}
      <StaticRouteDocumentHead />
    </>
  );
}
